import { AlertTriangle, RotateCw } from "lucide-react";
import { useEffect, useState } from "react";
import { isRouteErrorResponse, Link, useRouteError } from "react-router";
import { Button, Card } from "@/components/ui";
import { isDemo } from "@/lib/api";

import { t } from "@/lib/i18n";

const errorText = (e: unknown) => (isRouteErrorResponse(e) ? `${e.status} ${e.statusText}` : e instanceof Error ? e.message : String(e));
const byTranslation = (msg: string) => /not a child of this node|removeChild|insertBefore/i.test(msg);

/** Absturz an den Server melden (einmal je Fehlertext und Seite), damit wir ihn im Log sehen */
const reported = new Set<string>();
function report(e: unknown) {
  const message = errorText(e).slice(0, 500);
  const key = `${location.pathname}|${message}`;
  if (isDemo || reported.has(key)) return;
  reported.add(key);
  void fetch("/api/client-errors", {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message,
      stack: e instanceof Error ? (e.stack ?? "").slice(0, 2000) : undefined,
      path: location.pathname,
      translated: document.documentElement.classList.contains("translated-ltr") || document.documentElement.classList.contains("translated-rtl"),
    }),
  }).catch(() => undefined);
}

/** Fehlerseite statt der nackten Standardmeldung von React Router – mit „Neu laden“ und Hinweis bei Übersetzungs-Fehlern */
export function ErrorPage({ inShell = false }: { inShell?: boolean }) {
  const e = useRouteError();
  const [details, setDetails] = useState(false);
  const msg = errorText(e);
  useEffect(() => report(e), [e]);
  return (
    <div className={inShell ? "py-10" : "min-h-screen grid place-items-center bg-bg p-6"}>
      <Card className="mx-auto max-w-lg p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="size-5 shrink-0 text-warning mt-0.5" />
          <div className="min-w-0">
            <h1 className="text-[16px] font-semibold">{t("Hier ist etwas schiefgelaufen")}</h1>
            <p className="mt-1 text-[13px] text-fg-muted">{t("Die Seite konnte nicht angezeigt werden. Bitte lade sie neu – deine Daten sind davon nicht betroffen. Wir wurden automatisch benachrichtigt.")}</p>
            {byTranslation(msg) && (
              <p className="mt-2 text-[13px] text-fg-muted">
                {t("Häufige Ursache: die automatische Übersetzung des Browsers oder eine Browser-Erweiterung (z. B. Grammarly). Das Portal gibt es auf Deutsch und Englisch – die Sprache stellst du unten links im Menü um.")}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="primary" onClick={() => location.reload()}>
                <RotateCw className="size-4" />
                {t("Neu laden")}
              </Button>
              <Link to="/" reloadDocument>
                <Button variant="secondary">{t("Zur Übersicht")}</Button>
              </Link>
            </div>
            <button type="button" className="mt-4 text-[12px] text-fg-subtle underline" onClick={() => setDetails((d) => !d)}>
              {details ? t("Details ausblenden") : t("Technische Details")}
            </button>
            {details && <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-surface-2 p-2 text-[11px] text-fg-muted">{msg}</pre>}
          </div>
        </div>
      </Card>
    </div>
  );
}
